import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

/**
 * Combines multiple class names using clsx and twMerge for optimal Tailwind CSS usage
 * @param {...any} inputs - Class names to combine
 * @returns {string} Combined class string
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Debounces a function call to improve performance
 * @param {Function} fn - Function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Debounced function
 */
export function debounce<T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  
  return function(this: any, ...args: Parameters<T>) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    
    timeoutId = setTimeout(() => {
      fn.apply(this, args);
      timeoutId = null;
    }, delay);
  };
}

/**
 * Formats a number as currency
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: 'USD')
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Creates a throttled function that only invokes func at most once per threshold
 * @param {Function} fn - Function to throttle
 * @param {number} threshold - Threshold in milliseconds
 * @returns {Function} Throttled function
 */
export function throttle<T extends (...args: any[]) => any>(
  fn: T,
  threshold: number = 300
): (...args: Parameters<T>) => void {
  let lastCall = 0;
  
  return function(this: any, ...args: Parameters<T>) {
    const now = Date.now();
    
    if (now - lastCall >= threshold) {
      fn.apply(this, args);
      lastCall = now;
    }
  };
}

/**
 * Safely parses JSON without throwing exceptions
 * @param {string} value - JSON string to parse
 * @param {any} defaultValue - Default value to return if parsing fails
 * @returns {any} Parsed object or default value
 */
export function safeJsonParse<T>(value: string, defaultValue: T): T {
  try {
    return JSON.parse(value) as T;
  } catch (error) {
    return defaultValue;
  }
}

/**
 * Safely stringifies an object to JSON, handling circular references, Symbol values, functions and null values
 * @param {any} obj - Object to stringify
 * @returns {string} JSON string or empty object if stringification fails
 */
export function safeJsonStringify(obj: any): string {
  if (!obj) return '{}';
  
  try {
    // Handle circular references
    const getCircularReplacer = () => {
      const seen = new WeakSet();
      return (key: string, value: any) => {
        if (typeof value === 'object' && value !== null) {
          if (seen.has(value)) {
            return '[Circular Reference]';
          }
          seen.add(value);
        }
        return value;
      };
    };
    
    return JSON.stringify(obj, getCircularReplacer(), 2);
  } catch (error) {
    console.error('Error in safeJsonStringify:', error);
    return '{}';
  }
}

/**
 * Format a date string to a more readable format
 * @param date The date to format
 * @returns Formatted date string
 */
export function formatDate(date: string | Date): string {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (e) {
    return String(date);
  }
}
